import { Box, isLegacy, LeadDetailsDesignVersion, Text } from 'components'
import Icon from 'components/Icon'
import FunnelXIcon from 'assets/funnel-x-white.svg'
import styled, { useTheme } from 'styled-components'
import { ThemeColor, colors } from 'utils/colors'

const IconContainer = styled.div<{ bgColor?: ThemeColor }>`
  background-color: ${({ theme, bgColor }) => bgColor && theme.colors[bgColor]};
  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.white};
    }
  }
`
export type StatusGroup = 'open' | 'converted' | 'invalid' | 'lost' | 'deleted' | 'valid'

interface LeadStatusChipProps {
  statusGroup?: StatusGroup
  customBgColor?: ThemeColor
  customTextColor?: ThemeColor
  iconColor?: ThemeColor
  designVersion?: LeadDetailsDesignVersion
}

export type StatusUIConfig = {
  icon: JSX.Element | null
  bgChipColors: ThemeColor
  textColor?: ThemeColor
  backgroundColor?: string
  bgIconColor: string
  faIcon: string[] | null
  text: string
}

export const statusConfig: Record<StatusGroup, StatusUIConfig> = {
  converted: {
    icon: <Icon icon={['far', 'fa-trophy-star']} fontSize={16} color="white" />,
    faIcon: ['far', 'fa-trophy-star'],
    bgChipColors: 'green',
    textColor: 'white',
    backgroundColor: '#4CE2A7',
    bgIconColor: 'green',
    text: 'converted_lead_lbl',
  },
  deleted: {
    icon: <Icon icon={['far', 'trash-alt']} fontSize={16} color="red" />,
    faIcon: ['far', 'trash-alt'],
    bgChipColors: 'gray2',
    textColor: 'gray5',
    backgroundColor: '#E4E9F2',
    bgIconColor: 'red',
    text: 'deleted_lead_lbl',
  },
  invalid: {
    icon: <Icon icon={['fal', 'fa-phone-slash']} fontSize={16} color="white" />,
    faIcon: ['fal', 'fa-phone-slash'],
    bgChipColors: 'yellow1',
    textColor: 'white',
    backgroundColor: '#F1A153',
    bgIconColor: 'yellow1',
    text: 'invalid_lead_lbl',
  },
  lost: {
    icon: (
      <IconContainer bgColor="red">
        <FunnelXIcon />
      </IconContainer>
    ),
    faIcon: ['fal', 'filter-circle-xmark'],
    bgChipColors: 'red',
    textColor: 'white',
    backgroundColor: '#E24C4C',
    bgIconColor: 'red',
    text: 'lost_lead_lbl',
  },
  open: {
    icon: null,
    faIcon: null,
    bgChipColors: 'gray4',
    textColor: 'gray7',
    backgroundColor: 'transparent',
    bgIconColor: 'transparent',
    text: '',
  },
  valid: {
    icon: null,
    faIcon: null,
    bgChipColors: 'gray4',
    textColor: 'gray7',
    backgroundColor: 'transparent',
    bgIconColor: 'transparent',
    text: '',
  },
}

export default function LeadStatusChip({
  statusGroup = 'open',
  customBgColor,
  customTextColor,
  designVersion = 'v1',
}: LeadStatusChipProps) {
  const theme = useTheme()
  const { icon, bgChipColors, textColor } = statusConfig[statusGroup] || {}

  return (
    <Box
      border={`1px solid ${customBgColor || colors[bgChipColors]}`}
      borderRadius="4px"
      display="flex"
      alignItems="center"
      backgroundColor={customBgColor || colors[bgChipColors]}
      padding="2px 6px"
      marginRight="6px"
    >
      {icon}
      <Text
        color={(customTextColor || textColor || theme.colors.white) as ThemeColor}
        display="inline"
        marginLeft={icon ? '6px' : '0'}
        fontWeight="medium"
        fontSize={isLegacy(designVersion) ? '12px' : '14px'}
        isCapitalizedFirstLetter
      >
        {statusGroup}
      </Text>
    </Box>
  )
}
